import React, { useState, useEffect } from "react";
import { message } from "antd";
import { useTheme } from "../../../ThemeContext";
import { getCustomerDetails, updateCustomer } from "../onboarding-services";
import "./StepStyles.css";

const CustomerDetailsStep = ({ onPrevious, onNext, isLoading, setIsLoading }) => {
  const { themeMode } = useTheme();
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    taxId: "",
    street: "",
    city: "",
    state: "",
    country: "",
    zipCode: ""
  });
  
  const [errors, setErrors] = useState({});
  const [hasLoadedData, setHasLoadedData] = useState(false);

  // Load existing customer data on component mount
  useEffect(() => {
    loadCustomerData();
  }, []);



  const loadCustomerData = async () => {
    try {
      setIsLoading(true);
      console.log("🚀 Starting customer data load...");
      
      // Wait for API key to be available in session storage
      let attempts = 0;
      const maxAttempts = 10; // Max 3 seconds wait
      
      while (attempts < maxAttempts) {
        const apiKey = sessionStorage.getItem("api_key");
        if (apiKey) {
          console.log("🔑 API key found, proceeding with customer data load");
          break;
        }
        console.log(`⏳ Waiting for API key... Attempt ${attempts + 1}/${maxAttempts}`);
        await new Promise(resolve => setTimeout(resolve, 300)); // Wait 300ms
        attempts++;
      }
      
      const apiKey = sessionStorage.getItem("api_key");
      if (!apiKey) {
        console.warn("⚠️ API key not available after waiting, proceeding anyway");
      }
      
      console.log("📡 Making API call to get customer details...");
      const response = await getCustomerDetails();
      console.log("📨 API Response:", response);
      
      if (response.statusCode === "SUCCESS" && response.response) {
        const customer = response.response;
        console.log("🔍 Customer data:", customer);
        
        // Parse billingAddress if it exists (current format: "Pune,Maharashtra")
        let addressParts = {
          street: "",
          city: "",
          state: "",
          country: "",
          zipCode: ""
        };
        
        if (customer.billingAddress) {
          const parts = customer.billingAddress.split(',').map(part => part.trim());
          console.log("🏠 Address parts:", parts);
          
          // Handle current format: "Pune,Maharashtra"
          if (parts.length === 2) {
            addressParts = {
              street: "",
              city: parts[0] || "",
              state: parts[1] || "",
              country: "India", // Default country for current format
              zipCode: ""
            };
          } else if (parts.length >= 3) {
            // Handle full format if it exists: "street, city, state, zipCode, country"
            addressParts = {
              street: parts[0] || "",
              city: parts[1] || "",
              state: parts[2] || "",
              zipCode: parts[3] || "",
              country: parts[4] || ""
            };
          }
        }
        
        const populatedData = {
          name: customer.name || "",
          email: customer.email || "",
          taxId: customer.taxId || "",
          street: addressParts.street,
          city: addressParts.city,
          state: addressParts.state,
          country: addressParts.country,
          zipCode: addressParts.zipCode
        };
        
        console.log("✅ Customer data populated:", {
          name: customer.name,
          email: customer.email,
          taxId: customer.taxId,
          billingAddress: customer.billingAddress,
          parsedAddress: addressParts,
          finalFormData: populatedData
        });
        
        setFormData(populatedData);
        console.log("✅ Form data has been set!");
      } else {
        console.warn("⚠️ API response not in expected format:", response);
      }
      setHasLoadedData(true);
    } catch (error) {
      console.error("❌ Error loading customer data:", error);
      setHasLoadedData(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }
    
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }
    
    // Tax ID validation - required and exactly 9 digits
    if (!formData.taxId.trim()) {
      newErrors.taxId = "Tax ID is required";
    } else if (!/^\d{9}$/.test(formData.taxId.trim())) {
      newErrors.taxId = "Tax ID must be exactly 9 digits";
    }
    
    if (!formData.street.trim()) {
      newErrors.street = "Street is required";
    }
    
    if (!formData.city.trim()) {
      newErrors.city = "City is required";
    }
    
    if (!formData.state.trim()) {
      newErrors.state = "State is required";
    }
    
    if (!formData.country.trim()) {
      newErrors.country = "Country is required";
    }
    
    if (!formData.zipCode.trim()) {
      newErrors.zipCode = "Zip code is required";
    } else if (!/^\d{5}$/.test(formData.zipCode.trim())) {
      newErrors.zipCode = "Zip code must be exactly 5 digits";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = async () => {
    if (!validateForm()) return;

    try {
      setIsLoading(true);
      
      // Create the address string for billingAddress
      const billingAddress = `${formData.street}, ${formData.city}, ${formData.state}, ${formData.zipCode}, ${formData.country}`;
      
      const updateData = {
        name: formData.name,
        email: formData.email,
        taxId: formData.taxId,
        billingAddress: billingAddress
      };

      const response = await updateCustomer(updateData);
      
      if (response.statusCode === "SUCCESS") {
        message.success("Customer details saved successfully!");
        onNext();
      } else {
        message.error(response.message || "Failed to save customer details");
      }
    } catch (error) {
      console.error("Error saving customer details:", error);
      message.error("Failed to save customer details. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  if (!hasLoadedData) {
    return (
      <div className="step-loading">
        <div className="loading-spinner"></div>
        <p>Loading customer details...</p>
      </div>
    );
  }

  return (
    <div className={`step-container ${themeMode}`}>
      <div className="step-form">
        {/* Customer Details Section */}
        <div className="form-section">
          <div className="form-row">
            <div className="form-field">
              <label className="form-label">Name*</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                className={`form-input ${errors.name ? "error" : ""}`}
                disabled={isLoading}
              />
              <div className="form-error-container">
                {errors.name && <span className="form-error">{errors.name}</span>}
              </div>
            </div>
            
            <div className="form-field">
              <label className="form-label">Email ID*</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                className={`form-input ${errors.email ? "error" : ""}`}
                disabled={isLoading}
              />
              <div className="form-error-container">
                {errors.email && <span className="form-error">{errors.email}</span>}
              </div>
            </div>
            
            <div className="form-field">
              <label className="form-label">Tax ID*</label>
              <input
                type="text"
                value={formData.taxId}
                onChange={(e) => handleInputChange("taxId", e.target.value)}
                className={`form-input ${errors.taxId ? "error" : ""}`}
                disabled={isLoading}
                placeholder="Enter Tax ID"
              />
              <div className="form-error-container">
                {errors.taxId && <span className="form-error">{errors.taxId}</span>}
              </div>
            </div>
          </div>
        </div>

        {/* Customer Address Section */}
        <div className="form-section">
          <h3 className="form-section-title">Customer Address</h3>
          
          <div className="form-row">
            <div className="form-field">
              <label className="form-label">Street*</label>
              <input
                type="text"
                value={formData.street}
                onChange={(e) => handleInputChange("street", e.target.value)}
                className={`form-input ${errors.street ? "error" : ""}`}
                disabled={isLoading}
                placeholder="Street"
              />
              <div className="form-error-container">
                {errors.street && <span className="form-error">{errors.street}</span>}
              </div>
            </div>
            
            <div className="form-field">
              <label className="form-label">City*</label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => handleInputChange("city", e.target.value)}
                className={`form-input ${errors.city ? "error" : ""}`}
                disabled={isLoading}
              />
              <div className="form-error-container">
                {errors.city && <span className="form-error">{errors.city}</span>}
              </div>
            </div>
            
            <div className="form-field">
              <label className="form-label">State*</label>
              <input
                type="text"
                value={formData.state}
                onChange={(e) => handleInputChange("state", e.target.value)}
                className={`form-input ${errors.state ? "error" : ""}`}
                disabled={isLoading}
              />
              <div className="form-error-container">
                {errors.state && <span className="form-error">{errors.state}</span>}
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-field">
              <label className="form-label">Country*</label>
              <input
                type="text"
                value={formData.country}
                onChange={(e) => handleInputChange("country", e.target.value)}
                className={`form-input ${errors.country ? "error" : ""}`}
                disabled={isLoading}
              />
              <div className="form-error-container">
                {errors.country && <span className="form-error">{errors.country}</span>}
              </div>
            </div>
            
            <div className="form-field">
              <label className="form-label">Zip Code*</label>
              <input
                type="text"
                value={formData.zipCode}
                onChange={(e) => handleInputChange("zipCode", e.target.value)}
                className={`form-input ${errors.zipCode ? "error" : ""}`}
                disabled={isLoading}
              />
              <div className="form-error-container">
                {errors.zipCode && <span className="form-error">{errors.zipCode}</span>}
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="step-actions has-previous">
        <button
            className="btn-previous"
            onClick={onPrevious}
            disabled={isLoading}
          >
            Previous
          </button>
          <button
            className="btn-next"
            onClick={handleNext}
            disabled={isLoading}
          >
            {isLoading ? "Saving..." : "Save & Next"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomerDetailsStep; 
